import { Controller } from 'stimulus'
import SimpleroPusher from '~/common/helpers/simplero_pusher'
import { AjaxHelper } from '~/common/extensions/ajax_helpers'

const POLL_INTERVAL = 10_000
const MESSAGE_ID_PREFIX = 'chat_bot_message_'

// The safety net under everything the server pushes into an open conversation as a new message —
// a workflow run's question, its report. (A response waiting on spawned agents continues in place,
// so it recovers through its own streaming sync, not this.)
//
// A run talks to the person who started it through Pusher, and Pusher delivers at most once: a
// laptop that sleeps mid-import, a wifi hop, a tab the browser throttles — the socket goes away,
// comes back, and whatever was published in between is simply gone. That is how a question the
// agent is BLOCKED on ends up invisible until somebody reloads, with the strip above the composer
// still saying "running" while nothing is running at all.
//
// So the browser stops trusting that it heard everything and asks instead: on reconnect, when the
// tab comes back, and on a slow tick while a run is going. The server answers with whatever is
// missing (ChatBot::AgenticWorkflowRunsController#sync), and asking twice costs nothing — a
// message already on the page is left alone.
//
// Lives on the progress strip's container rather than the conversation, because that container is
// what the run's own push morphs: `live` arrives on the morphed attributes, so a run that starts
// mid-conversation turns the net on without anything else having to notice.
export default class extends Controller {
  static values = { url: String, live: Boolean }

  connect() {
    this.connected = true
    document.addEventListener('visibilitychange', this.#onVisibilityChange)
    this.#withPusherConnection((connection) =>
      connection.bind('connected', this.#onReconnect)
    )
    this.#refreshPolling()
  }

  disconnect() {
    this.connected = false
    document.removeEventListener('visibilitychange', this.#onVisibilityChange)
    this.#withPusherConnection((connection) =>
      connection.unbind('connected', this.#onReconnect)
    )
    this.#stopPolling()
  }

  // A page with no working Pusher is the very thing this exists for, so it must not be the thing
  // that stops it starting: reaching the connection builds the client, and that can throw.
  #withPusherConnection(callback) {
    try {
      callback(SimpleroPusher.connection)
    } catch {
      // No socket to hang a reconnect on. The tick and the tab coming back still cover it.
    }
  }

  // A run that starts — or ends — mid-conversation: the push that morphs this container carries
  // the new value, so the net follows the run rather than the page load. Stimulus also calls this
  // on mount, before connect, which is why it waits for one.
  liveValueChanged() {
    if (this.connected) this.#refreshPolling()
  }

  #onVisibilityChange = () => {
    if (!document.hidden && this.liveValue) this.#sync()
  }

  // Every push published while the socket was down is unrecoverable, so a reconnect is exactly
  // the moment this page is most likely to be out of date.
  #onReconnect = () => {
    if (this.liveValue) this.#sync()
  }

  #refreshPolling() {
    if (this.liveValue) this.#startPolling()
    else this.#stopPolling()
  }

  #startPolling() {
    if (this.timer) return

    this.#sync()
    this.timer = setInterval(() => this.#sync(), POLL_INTERVAL)
  }

  #stopPolling() {
    if (this.timer) clearInterval(this.timer)
    this.timer = null
  }

  async #sync() {
    if (this.syncing || !this.urlValue) return

    this.syncing = true
    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set('after_message_id', this.#lastMessageId())

      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) return

      const data = await response.json()
      AjaxHelper.doUpdate(data)
      // The morph above rewrites this element's attributes from the strip's own render, and the
      // strip only knows what is *running*. The payload knows the rest — that the bot is still
      // writing the turn a finished run or a finished set of agents woke it into — so it is the
      // answer, and writing it back is what keeps the morph from ending the asking one turn early.
      this.liveValue = Boolean(data.live)
    } catch {
      // A dropped request just means this tick saw nothing. The next one asks again.
    } finally {
      this.syncing = false
    }
  }

  // The newest message already drawn, so a catch-up costs what is missing rather than the whole
  // transcript. Scoped to this conversation: a side panel can have another one open beside it.
  #lastMessageId() {
    const conversation =
      this.element.closest('.js-chat-bot-conversation') || document
    const ids = Array.from(
      conversation.querySelectorAll(`[id^="${MESSAGE_ID_PREFIX}"]`)
    ).map((element) => parseInt(element.id.slice(MESSAGE_ID_PREFIX.length), 10))
    const known = ids.filter((id) => Number.isInteger(id))

    return known.length ? Math.max(...known) : 0
  }
}
